import { keepPreviousData, queryOptions, useQuery } from '@tanstack/react-query';

import { getBlogSearchQuery } from '@/logic/blog/blogSearch.utils';
import { QueryKeys } from '@/network/query.types';

import { client, unwrapResponse } from '../client';

interface BlogSearchQuery {
  page?: number;
  categorySlug?: string;
  q?: string;
}

// query options

export const fetchBlogQueryOptions = () =>
  queryOptions({
    queryKey: [QueryKeys.blog],
    queryFn: () => client.GET('/api/v1/blog').then(unwrapResponse),
  });

export const fetchBlogCategoryQueryOptions = (slug: string) =>
  queryOptions({
    queryKey: [QueryKeys.blogCategory, slug],
    queryFn: () =>
      client
        .GET('/api/v1/blog/category/{category_slug}', {
          params: {
            path: {
              category_slug: slug,
            },
          },
        })
        .then(unwrapResponse),
  });

export const fetchBlogSearchQueryOptions = (query: BlogSearchQuery) =>
  queryOptions({
    queryKey: [QueryKeys.blogSearch, query],
    queryFn: () => {
      const searchQuery = getBlogSearchQuery(query);
      return client
        .GET('/api/v1/blog/search', {
          params: {
            query: searchQuery,
          },
        })
        .then(unwrapResponse);
    },
  });

export const fetchBlogPostQueryOptions = (categorySlug: string, postSlug: string) =>
  queryOptions({
    queryKey: [QueryKeys.blogPost, categorySlug, postSlug],
    queryFn: () =>
      client
        .GET('/api/v1/blog/category/{category_slug}/post/{post_slug}', {
          params: {
            path: {
              category_slug: categorySlug,
              post_slug: postSlug,
            },
          },
        })
        .then(unwrapResponse),
  });

export const fetchBlogPostCategoryQueryOptions = (categorySlug: string) =>
  queryOptions({
    queryKey: [QueryKeys.blogPostCategory, categorySlug],
    queryFn: () =>
      client
        .GET('/api/v1/blog/search', {
          params: {
            query: {
              category_slug: categorySlug,
              limit: 10,
            },
          },
        })
        .then(unwrapResponse),
  });

// queries
export function useBlogQuery() {
  return useQuery(fetchBlogQueryOptions());
}

export function useBlogCategoryQuery(slug: string) {
  return useQuery(fetchBlogCategoryQueryOptions(slug));
}

export function useSearchBlogQuery(query: BlogSearchQuery) {
  return useQuery({
    ...fetchBlogSearchQueryOptions(query),
    placeholderData: keepPreviousData,
  });
}

export function useBlogPostQuery(categorySlug: string, postSlug: string) {
  return useQuery({
    ...fetchBlogPostQueryOptions(categorySlug, postSlug),
    placeholderData: keepPreviousData,
  });
}

export function useBlogPostCategoryQuery(categorySlug: string) {
  return useQuery(fetchBlogPostCategoryQueryOptions(categorySlug));
}
